import { Divider, Stack, Text } from '@ltvco/refresh-lib/theme';
import { ChatBubbleOutline, VisibilityOutlined } from '@mui/icons-material';
import { Question } from 'api/requests/communityBuzz/interfaces';
import { calculateTimeSince } from './utils';
import { BUMPER_AI_USERNAME, isBumperAIContributor } from './constants';
import { useContext, useCallback } from 'react';
import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { COMMUNITY_BUZZ_GA4_EVENTS } from 'components/CommunityBuzz/constants';

interface QuestionDetailsProps {
  isModal?: boolean;
  question?: Question;
  onOpenQADisplayModal?: () => void;
  setSelectedQuestion?: (question?: Question) => void;
}
export const QuestionDetails = ({
  isModal = false,
  question,
  onOpenQADisplayModal,
  setSelectedQuestion,
}: QuestionDetailsProps) => {
  const { trackEventGA4 } = useContext(AppConfig);
  const hasQuestion = Boolean(question);

  const handleOpenQADisplayModal = () => {
    if (!question) {
      return;
    }
    onOpenQADisplayModal?.();
    setSelectedQuestion?.(question);
    const { communityBuzzViewQuestionAction } = COMMUNITY_BUZZ_GA4_EVENTS;
    trackEventGA4({
      event_name: communityBuzzViewQuestionAction.name,
      type: communityBuzzViewQuestionAction.type,
    });
  };

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!hasQuestion) {
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleOpenQADisplayModal();
      }
    },
    [handleOpenQADisplayModal, hasQuestion],
  );

  return (
    <Stack width="100%">
      <Stack
        width="100%"
        direction="row"
        spacing={2}
        alignItems="flex-start"
        onClick={handleOpenQADisplayModal}
        onKeyDown={handleKeyDown}
        role={hasQuestion ? 'button' : undefined}
        tabIndex={hasQuestion ? 0 : -1}
        aria-disabled={!hasQuestion}
        sx={{ cursor: hasQuestion ? 'pointer' : 'default' }}
      >
        <Stack spacing={1} direction="column" sx={{ flex: 1, minWidth: 0 }}>
          <Text variant="h5" mb={0}>
            {question?.body_md}
          </Text>
          <Stack
            direction={isModal ? 'column' : { xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={
              isModal ? 'flex-start' : { xs: 'flex-start', sm: 'center' }
            }
            justifyContent="space-between"
            sx={{ width: '100%' }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Text variant="body2" color="text.secondary">
                {calculateTimeSince(question?.created_at)}
              </Text>
              <Text variant="body2" color="text.secondary">
                •{' '}
                {isBumperAIContributor(question?.source, question?.username)
                  ? BUMPER_AI_USERNAME
                  : question?.username}
              </Text>
            </Stack>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ ml: 'auto' }}
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                <ChatBubbleOutline
                  sx={{ fontSize: '1rem', color: 'text.secondary' }}
                />
                <Text variant="body2" color="text.secondary">
                  {question?.answers_count ?? 0}
                </Text>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <VisibilityOutlined
                  sx={{ fontSize: '1rem', color: 'text.secondary' }}
                />
                <Text variant="body2" color="text.secondary">
                  {question?.views ?? 0}
                </Text>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <Divider sx={{ mt: 2 }} />
    </Stack>
  );
};
